(() => {
  const vehicleKeys = ['make', 'model', 'year'];
  const sortValues = (values) => [...new Set(values)].filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const parseProducts = (products) => products.map(product => ({
    ...product,
    tuples: product.rules.split(';').filter(Boolean).map(row => {
      const [make, model, years = ''] = row.trim().split('|');
      return { make, model, years: years.split(',').map(year => year.trim()).filter(Boolean) };
    })
  }));
  const matchesVehicle = (product, values) => product.tuples.some(tuple =>
    (!values.make || tuple.make === values.make) &&
    (!values.model || tuple.model === values.model) &&
    (!values.year || tuple.years.includes(values.year))
  );
  async function loadCatalog(path, sort, view, signal) {
    async function page(number) {
      const url = new URL(path, location.origin);
      url.search = '';
      url.searchParams.set('view', view);
      url.searchParams.set('page', number);
      if (sort) url.searchParams.set('sort_by', sort);
      const response = await fetch(url, { signal, cache: 'no-store' });
      if (!response.ok) throw new Error('Catalog request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = doc.getElementById('motors-catalog-data');
      if (!source) throw new Error('Catalog data missing');
      return JSON.parse(source.textContent);
    }
    const first = await page(1);
    if (first.total > 25000 || first.pages > 500) throw new Error('Catalog exceeds supported pagination');
    const pages = [first];
    for (let start = 2; start <= first.pages; start += 3) {
      pages.push(...await Promise.all(Array.from({ length: Math.min(3, first.pages - start + 1) }, (_, index) => page(start + index))));
    }
    const products = pages.flatMap(result => result.products);
    if (products.length !== first.total || new Set(products.map(product => product.id)).size !== products.length) throw new Error('Incomplete catalog');
    return parseProducts(products);
  }
  function populateOptions(select, values, label, selected, preserveYear = false) {
    select.replaceChildren(new Option(label, ''));
    for (const value of sortValues(values)) select.add(new Option(value, value));
    if (preserveYear && selected && !values.includes(selected) && /^\d{4}$/.test(selected)) select.add(new Option(selected, selected));
    select.value = selected || '';
  }
  function populateVehicle(controls, products, values, labels) {
    const tuples = products.flatMap(product => product.tuples);
    populateOptions(controls.make, tuples.map(tuple => tuple.make), labels.make, values.make);
    const make = controls.make.value;
    populateOptions(controls.model, tuples.filter(tuple => tuple.make === make).map(tuple => tuple.model), labels.model, make ? values.model : '');
    const model = controls.model.value;
    populateOptions(controls.year, tuples.filter(tuple => tuple.make === make && tuple.model === model).flatMap(tuple => tuple.years), labels.year, make && model ? values.year : '', true);
    controls.make.disabled = false;
    controls.model.disabled = !make;
    controls.year.disabled = !(make && model);
  }
  function readVehicle() {
    const params = new URL(location.href).searchParams;
    return Object.fromEntries(vehicleKeys.map(key => [key, params.get('mf_' + key) || '']));
  }
  function vehicleUrl(base, values) {
    const url = new URL(location.href);
    if (base) url.pathname = new URL(base, location.origin).pathname;
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('mf_page');
    [...url.searchParams.keys()].filter(key => key.startsWith('filter.') && key !== 'filter.v.availability').forEach(key => url.searchParams.delete(key));
    for (const key of vehicleKeys) {
      if (values[key]) url.searchParams.set('mf_' + key, values[key]);
      else url.searchParams.delete('mf_' + key);
    }
    return url;
  }
  if (!customElements.get('motors-vehicle-search')) {
    customElements.define('motors-vehicle-search', class extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        this.labels = JSON.parse(this.querySelector('[data-vehicle-labels]').textContent);
        this.controls = Object.fromEntries([...this.querySelectorAll('[data-vehicle-field]')].map(el => [el.dataset.vehicleField, el]));
        this.submitButton = this.querySelector('[data-vehicle-submit]');
        this.clearButton = this.querySelector('[data-clear-vehicle]');
        this.status = this.querySelector('[data-vehicle-status]');
        this.retry = this.querySelector('[data-vehicle-retry]');
        this.querySelector('form').addEventListener('submit', event => {
          event.preventDefault();
          if (!this.complete()) return;
          const catalog = document.querySelector('motors-catalog');
          if (this.dataset.mode === 'catalog' && catalog?.products) catalog.setVehicle(this.values());
          else location.assign(vehicleUrl(this.dataset.url, this.values()).href);
        });
        Object.entries(this.controls).forEach(([key, el]) => el.addEventListener('change', () => {
          if (key === 'make') { this.controls.model.value = ''; this.controls.year.value = ''; }
          if (key === 'model') this.controls.year.value = '';
          this.populate(this.values());
          if (this.dataset.mode === 'home') {
            const url = vehicleUrl(null, this.values());
            history.replaceState({}, '', url);
          }
        }));
        this.clearButton.addEventListener('click', () => {
          const catalog = document.querySelector('motors-catalog');
          if (this.dataset.mode === 'catalog' && catalog?.products) catalog.clearVehicle();
          else {
            this.populate({});
            history.replaceState({}, '', vehicleUrl(null, {}));
          }
        });
        this.retry.addEventListener('click', () => {
          if (this.dataset.mode === 'catalog') document.querySelector('motors-catalog')?.load();
          else this.load();
        });
        this.onCatalog = event => {
          this.products = event.detail.products;
          this.populate(event.detail.values);
        };
        this.onHistory = () => { if (this.products && this.dataset.mode === 'home') this.populate(readVehicle()); };
        document.addEventListener('motors:catalog-change', this.onCatalog);
        addEventListener('popstate', this.onHistory);
        addEventListener('pageshow', this.onHistory);
        if (this.dataset.mode === 'home') this.load();
        else {
          const catalog = document.querySelector('motors-catalog');
          if (catalog?.products) { this.products = catalog.products; this.populate(catalog.vehicle()); }
        }
      }
      disconnectedCallback() {
        document.removeEventListener('motors:catalog-change', this.onCatalog);
        removeEventListener('popstate', this.onHistory);
        removeEventListener('pageshow', this.onHistory);
        this.abort?.abort();
      }
      values() { return Object.fromEntries(Object.entries(this.controls).map(([key, el]) => [key, el.value])); }
      complete() { return vehicleKeys.every(key => this.controls[key].value && !this.controls[key].disabled); }
      populate(values) {
        if (!this.products) return;
        populateVehicle(this.controls, this.products, values, { make: this.labels.select_make, model: this.labels.select_model, year: this.labels.select_year });
        this.submitButton.disabled = !this.complete();
        this.clearButton.disabled = !vehicleKeys.some(key => this.controls[key].value);
        this.status.textContent = this.labels.vehicle_help;
        this.retry.hidden = true;
      }
      busy() {
        Object.values(this.controls).forEach(el => { el.disabled = true; });
        this.submitButton.disabled = true;
        this.clearButton.disabled = true;
        this.status.textContent = this.labels.vehicle_loading;
      }
      failed() { this.status.textContent = this.labels.vehicle_error; this.retry.hidden = false; }
      async load() {
        this.abort?.abort();
        this.abort = new AbortController();
        const controller = this.abort;
        this.busy();
        try {
          this.products = await loadCatalog(this.dataset.url, '', 'motors-vehicles', controller.signal);
          if (!controller.signal.aborted) this.populate(readVehicle());
        } catch (error) { if (!controller.signal.aborted) this.failed(); }
      }
    });
  }
  if (!customElements.get('motors-catalog')) {
    customElements.define('motors-catalog', class extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        this.labels = JSON.parse(document.getElementById('MotorsCatalogLabels').textContent);
        this.collectionMake = this.dataset.collectionMake || '';
        this.makeCollections = JSON.parse(this.querySelector('[data-make-collections]').textContent);
        this.controls = Object.fromEntries([...this.querySelectorAll('[data-filter]')].map(el => [el.dataset.filter, el]));
        this.sort = this.querySelector('[data-sort]');
        this.grid = this.querySelector('[data-grid]');
        this.native = this.parentElement.querySelector('[data-motors-native]');
        this.loading = this.parentElement.querySelector('[data-motors-loading]');
        this.details = this.querySelector('details');
        this.media = matchMedia('(min-width:750px)');
        this.onMedia = () => { this.details.open = this.media.matches; };
        this.onMedia();
        this.media.addEventListener('change', this.onMedia);
        Object.entries(this.controls).forEach(([key, el]) => el.addEventListener('change', () => {
          if (key === 'make' && this.collectionMake && this.changeMake(el.value)) return;
          if (key === 'make') { this.controls.model.value = ''; this.controls.year.value = ''; }
          if (key === 'model') this.controls.year.value = '';
          this.page = 1;
          this.render(true);
        }));
        this.sort.addEventListener('change', () => { this.page = 1; this.writeUrl(); this.load(); });
        this.querySelector('[data-clear]').addEventListener('click', () => this.clear());
        this.querySelector('[data-change-vehicle]').addEventListener('click', () => {
          this.details.open = true;
          const field = this.controls.make;
          field?.focus();
          field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
        this.querySelector('[data-empty-clear]').addEventListener('click', () => this.clearVehicle());
        this.onPop = () => this.load();
        addEventListener('popstate', this.onPop);
        this.load();
      }
      disconnectedCallback() { removeEventListener('popstate', this.onPop); this.media?.removeEventListener('change', this.onMedia); this.abort?.abort(); }
      changeMake(make) {
        if (!make || make === this.collectionMake || !this.makeCollections[make]) return false;
        const url = vehicleUrl(this.makeCollections[make], { make });
        url.searchParams.delete('mf_availability');
        if (this.controls.availability.value) url.searchParams.set('mf_availability', this.controls.availability.value);
        url.searchParams.set('sort_by', this.sort.value);
        location.assign(url.href);
        return true;
      }
      vehicle() { return Object.fromEntries(vehicleKeys.map(key => [key, this.controls[key].value])); }
      setVehicle(values) {
        if (this.collectionMake && values.make && values.make !== this.collectionMake && this.changeMake(values.make)) return;
        this.state = { ...values, ...(this.collectionMake ? { make: this.collectionMake } : {}) };
        this.page = 1;
        this.render(true);
      }
      clearVehicle() { this.setVehicle({}); }
      readUrl() {
        const params = new URL(location.href).searchParams;
        this.state = Object.fromEntries([...vehicleKeys, 'availability'].map(key => [key, params.get('mf_' + key) || '']));
        if (this.collectionMake) this.state.make = this.collectionMake;
        this.page = Math.max(1, parseInt(params.get('mf_page') || params.get('page') || '1', 10) || 1);
        const sort = params.get('sort_by') || this.dataset.defaultSort;
        if ([...this.sort.options].some(option => option.value === sort)) this.sort.value = sort;
      }
      writeUrl(replace = false) {
        const url = vehicleUrl(null, this.appliedVehicle || this.vehicle());
        [...url.searchParams.keys()].filter(key => key.startsWith('filter.')).forEach(key => url.searchParams.delete(key));
        if (this.controls.availability.value) url.searchParams.set('mf_availability', this.controls.availability.value);
        else url.searchParams.delete('mf_availability');
        url.searchParams.set('sort_by', this.sort.value);
        if (this.page > 1) url.searchParams.set('mf_page', this.page);
        if (url.href !== location.href) history[replace ? 'replaceState' : 'pushState']({}, '', url);
      }
      async load() {
        this.abort?.abort();
        const controller = new AbortController();
        this.abort = controller;
        this.readUrl();
        this.loading.hidden = false;
        this.loading.textContent = this.labels.loading;
        this.querySelector('.motors-catalog__results').setAttribute('aria-busy', 'true');
        for (const el of [...Object.values(this.controls), this.sort]) el.disabled = true;
        const search = document.querySelector('motors-vehicle-search[data-mode="catalog"]');
        search?.busy();
        try {
          this.products = await loadCatalog(this.dataset.url, this.sort.value, 'motors-data', controller.signal);
          if (controller.signal.aborted) return;
          const params = new URL(location.href).searchParams;
          if (!params.has('mf_availability') && params.has('filter.v.availability')) this.state.availability = params.get('filter.v.availability');
          this.controls.availability.value = this.state.availability;
          this.controls.availability.disabled = false;
          this.sort.disabled = false;
          this.render(false);
          this.writeUrl(true);
          this.hidden = false;
          this.native.hidden = true;
          this.loading.hidden = true;
        } catch (error) {
          if (controller.signal.aborted) return;
          this.hidden = true;
          this.native.hidden = false;
          this.loading.textContent = this.labels.error;
          this.loading.hidden = false;
          search?.failed();
        } finally { if (!controller.signal.aborted) this.querySelector('.motors-catalog__results').setAttribute('aria-busy', 'false'); }
      }
      populate() {
        const requested = this.state || this.vehicle();
        if (this.collectionMake) requested.make = this.collectionMake;
        populateVehicle(this.controls, this.products, requested, { make: this.labels.all_makes, model: this.labels.all_models, year: this.labels.all_years });
        if (this.collectionMake) {
          populateOptions(this.controls.make, Object.keys(this.makeCollections), this.labels.all_makes, this.collectionMake);
          this.controls.make.querySelector('option[value=""]').remove();
        }
        const chosen = this.vehicle();
        this.appliedVehicle = requested.make && requested.make !== chosen.make || requested.model && requested.model !== chosen.model ? requested : chosen;
        this.state = null;
      }
      clear() { for (const el of Object.values(this.controls)) el.value = ''; this.page = 1; this.render(true); }
      render(push) {
        this.populate();
        const values = { ...this.appliedVehicle, availability: this.controls.availability.value };
        const matches = this.products.filter(product => {
          if (values.availability && product.available !== (values.availability === '1')) return false;
          return (this.collectionMake && !values.model && !values.year) || !vehicleKeys.some(key => values[key]) || matchesVehicle(product, values);
        });
        const size = Number(this.dataset.pageSize) || 16;
        const pages = Math.max(1, Math.ceil(matches.length / size));
        this.page = Math.min(this.page, pages);
        this.querySelector('[data-count]').textContent = this.labels.count.replace('[count]', matches.length);
        this.querySelector('[data-empty]').hidden = matches.length > 0;
        this.grid.replaceChildren();
        for (const product of matches.slice((this.page - 1) * size, this.page * size)) {
          const li = document.createElement('li');
          li.className = 'grid__item';
          li.dataset.productId = product.id;
          li.innerHTML = product.card;
          this.grid.append(li);
        }
        const active = this.querySelector('[data-active]');
        active.replaceChildren();
        for (const [key, value] of Object.entries(values)) if (value && !(this.collectionMake && key === 'make')) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = this.labels[key] + ': ' + (key === 'availability' ? this.labels[value === '1' ? 'in_stock' : 'out_of_stock'] : value) + ' ×';
          button.addEventListener('click', () => {
            this.controls[key].value = '';
            if (key === 'make') { this.controls.model.value = ''; this.controls.year.value = ''; }
            if (key === 'model') this.controls.year.value = '';
            this.page = 1;
            this.render(true);
          });
          active.append(button);
        }
        if (active.childElementCount) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = this.labels.clear;
          button.addEventListener('click', () => this.clear());
          active.append(button);
        }
        if (push) this.writeUrl();
        const nav = this.querySelector('[data-pagination]');
        nav.replaceChildren();
        if (pages > 1) for (let number = 1; number <= pages; number++) {
          if (pages > 9 && number !== 1 && number !== pages && Math.abs(number - this.page) > 2) continue;
          const link = document.createElement('a');
          const url = vehicleUrl(null, values);
          if (values.availability) url.searchParams.set('mf_availability', values.availability);
          else url.searchParams.delete('mf_availability');
          url.searchParams.set('sort_by', this.sort.value);
          url.searchParams.set('mf_page', number);
          link.href = url.href;
          link.textContent = number;
          link.setAttribute('aria-label', this.labels.page.replace('[page]', number));
          if (number === this.page) link.setAttribute('aria-current', 'page');
          link.addEventListener('click', event => {
            if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            this.page = number;
            this.render(true);
            this.querySelector('.motors-catalog__toolbar').scrollIntoView({ block: 'start' });
          });
          nav.append(link);
        }
        document.dispatchEvent(new CustomEvent('motors:catalog-change', { detail: { products: this.products, values } }));
      }
    });
  }
})();

